/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'styles/rsuite-styles.less';
import 'react-toastify/dist/ReactToastify.css';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import 'react-multi-carousel/lib/styles.css';

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { PostHogProvider } from '@posthog/react';

// Use consistent styling
import 'sanitize.css/sanitize.css';

// Import root app
import { App } from 'containers/App';

import { HelmetProvider } from 'react-helmet-async';

import { configureAppStore } from 'store/configureStore';

import reportWebVitals from 'reportWebVitals';
import posthog from 'posthog-js';
import {
  getAnalyticsAppEnv,
  getAnalyticsBuildSha,
  setAnalyticsEnabled,
  track,
} from 'analytics/posthog';
import config from 'config';

// Initialize languages
import './locales/i18n';

const store = configureAppStore();
const MOUNT_NODE = document.getElementById('root') as HTMLElement;
const appEnv = getAnalyticsAppEnv();
const buildSha = getAnalyticsBuildSha();

const AppTree = (
  <HelmetProvider>
    <React.StrictMode>
      <App />
    </React.StrictMode>
  </HelmetProvider>
);

const renderApp = (withAnalytics = false) => {
  ReactDOM.render(
    // @ts-ignore - Redux Provider children type issue with React 18
    <Provider store={store}>
      {withAnalytics ? (
        <PostHogProvider client={posthog}>{AppTree}</PostHogProvider>
      ) : (
        AppTree
      )}
    </Provider>,
    MOUNT_NODE,
  );
};

const initializePostHog = async () => {
  try {
    const response = await fetch(`${config.serverUrl}/api/v1/config`);
    if (!response.ok) {
      throw new Error(`Failed to load app config: ${response.status}`);
    }

    const appConfig: { posthogToken?: string; posthogHost?: string } =
      await response.json();

    if (appConfig.posthogToken) {
      // Useful for confirming runtime config arrived without exposing the secret.
      console.log('[analytics] obtained posthog token');
    }

    if (appConfig.posthogHost) {
      console.log('[analytics] obtained posthog host');
    }

    if (!appConfig.posthogToken || !appConfig.posthogHost) {
      setAnalyticsEnabled(false);
      renderApp(false);
      return;
    }

    posthog.init(appConfig.posthogToken, {
      api_host: appConfig.posthogHost,
      defaults: '2026-01-30',
      capture_pageview: 'history_change',
      capture_pageleave: true,
      person_profiles: 'identified_only',
      disable_session_recording: true,
      loaded: ph => {
        ph.register({
          app_name: 'tv-testing-lab',
          ui_version: 'ui-1.0',
          app_env: appEnv,
          build_sha: buildSha,
        });
      },
    });
    setAnalyticsEnabled(true);
    renderApp(true);
    return;
  } catch (error) {
    // Keep the app booting even if analytics config fails to load.
  }

  setAnalyticsEnabled(false);
  renderApp(false);
};

initializePostHog();

// Hot reloadable translation json files
if (module.hot) {
  module.hot.accept(['./locales/i18n'], () => {
    // No need to render the App again because i18next works with the hooks
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(metric => {
  track('web vitals measured', {
    metric_name: metric.name,
    value: metric.value,
    rating: (metric as any).rating,
  });
});
